import { type PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { addWaypointToTrail, addWaypointToMap } from './api/addWaypoint';
import { deleteAllWaypoints as deleteAllWaypointsServer, deleteWaypoint as deleteWaypointServer } from './api/deleteWaypoints';
import {
  UpdateWaypointPayload,
  reorderWaypoints as reorderWaypointsServer,
  updateAllWaypoints,
  updateWaypoint as updateWaypointServer
} from './api/editWaypoints';
import { getMapWaypoints } from './api/getWaypoints';
import Waypoint, { WaypointId } from './types/Waypoint';

export type NewWaypointType = 'trail' | 'map';

export type WaypointsState = {
  /**
   * at_map_id property of the waypoints. Note this may differ from a trail's associated mapId.
   */
  atMapId?: number;
  /**
   * Generic load state for any waypoint operation.
   */
  loading: boolean;
  /**
   * Any errors that occurred during the last waypoint operation.
   */
  errors: string[];
  /**
   * The currently displayed waypoints, where order is respected.
   */
  waypoints?: (Waypoint & { showInTranslatedLang?: boolean })[];
};

const initialState: WaypointsState = {
  loading: false,
  errors: [],
  waypoints: undefined
};

function getErrorMessages(error: { data: { errors: { message: string }[] } }): string[] {
  return error?.data?.errors?.map((e: { message: string }) => e.message) ?? [];
}

/**
 * Used to initialize or set a new array of waypoints.
 * This will overwrite both the current waypoints and the atMapId.
 */
export const initializeWaypoints = createAsyncThunk('waypoints/initializeWaypoints', ({ mapId }: { mapId: number }) => {
  return getMapWaypoints(mapId);
});

export const addWaypoint = createAsyncThunk(
  'waypoints/addWaypoint',
  (
    {
      waypoint,
      id,
      type
    }: {
      waypoint: Pick<Waypoint, 'description' | 'location' | 'name' | 'waypointDisplayProperty'>;
      id: number /* mapId or trailId */;
      type: NewWaypointType;
    },
    { rejectWithValue }
  ) => {
    if (type === 'trail') {
      return addWaypointToTrail(id, waypoint).catch(err => rejectWithValue(getErrorMessages(err)));
    } else {
      const { description, location, name, waypointDisplayProperty } = waypoint;
      return addWaypointToMap(id, { description, location, name, showTitle: waypointDisplayProperty.showTitle }).catch(err =>
        rejectWithValue(getErrorMessages(err))
      );
    }
  }
);

export const updateWaypoint = createAsyncThunk(
  'waypoints/editWaypoint',
  async ({ waypointId, waypointChanges }: { waypointId: number; waypointChanges: UpdateWaypointPayload }, { getState, rejectWithValue }) => {
    const {
      waypoints: { atMapId }
    } = getState() as { waypoints: WaypointsState };
    return updateWaypointServer(atMapId!, waypointId, waypointChanges).catch(err => rejectWithValue(getErrorMessages(err)));
  }
);

export const deleteWaypoint = createAsyncThunk(
  'waypoints/deleteWaypoint',
  ({ waypointId }: { waypointId: WaypointId }, { getState, rejectWithValue }) => {
    const {
      waypoints: { atMapId }
    } = getState() as { waypoints: WaypointsState };
    return deleteWaypointServer(atMapId!, waypointId).catch(err => rejectWithValue(getErrorMessages(err)));
  }
);

export const persistWaypointOrder = createAsyncThunk(
  'waypoints/persistWaypointOrder',
  ({ orderedWaypointIds }: { orderedWaypointIds: WaypointId[] }, { getState, rejectWithValue }) => {
    const {
      waypoints: { atMapId }
    } = getState() as { waypoints: WaypointsState };
    return reorderWaypointsServer(atMapId!, orderedWaypointIds).catch(err => rejectWithValue(getErrorMessages(err)));
  }
);

export const toggleShowAllTitles = createAsyncThunk(
  'waypoints/updateAllWaypoints',
  ({ showTitle }: { showTitle: boolean }, { getState, rejectWithValue }) => {
    const {
      waypoints: { atMapId }
    } = getState() as { waypoints: WaypointsState };
    return updateAllWaypoints(atMapId!, { showTitle }).catch(err => rejectWithValue(getErrorMessages(err)));
  }
);

export const deleteAllWaypoints = createAsyncThunk('waypoints/deleteAllWaypoints', (_, { getState, rejectWithValue }) => {
  const {
    waypoints: { atMapId }
  } = getState() as { waypoints: WaypointsState };
  return deleteAllWaypointsServer(atMapId!).catch(err => rejectWithValue(getErrorMessages(err)));
});

export const waypointsSlice = createSlice({
  name: 'waypoints',
  initialState,
  reducers: {
    reorderWaypoints: (state, action: PayloadAction<Waypoint[]>) => {
      if (action.payload.length !== state.waypoints?.length) {
        throw new Error('Reordered waypoints are different. If this is intentional, use a different operation.');
      }
      state.waypoints = action.payload;
    },
    toggleShowInTranslatedLang: (state, action: PayloadAction<{ idx: number }>) => {
      if (!state.waypoints) {
        return;
      }

      const { idx } = action.payload;
      if (idx < 0 || idx >= state.waypoints.length) {
        throw new Error('Invalid waypoint index');
      }
      state.waypoints[idx].showInTranslatedLang = !state.waypoints[idx].showInTranslatedLang;
    }
  },
  extraReducers: builder => {
    // initializeWaypoints
    builder.addCase(initializeWaypoints.pending, state => {
      state.loading = true;
      state.errors = [];
    });
    builder.addCase(initializeWaypoints.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.waypoints = payload.sort((a, b) => a.order - b.order) ?? [];
      state.atMapId = payload[0]?.at_map_id;
    });
    builder.addCase(initializeWaypoints.rejected, (state, { error }) => {
      state.loading = false;
      state.errors.push(error?.message ?? '');
    });

    // addWaypoint
    builder.addCase(addWaypoint.pending, state => {
      state.loading = true;
      state.errors = [];
    });
    builder.addCase(addWaypoint.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.waypoints?.push(payload.waypoints[0]);
      if (!state.atMapId) {
        state.atMapId = payload.waypoints[0].at_map_id;
      }
    });
    builder.addCase(addWaypoint.rejected, (state, { payload }) => {
      state.loading = false;
      if (payload) {
        state.errors = state.errors.concat(payload as string[]);
      }
    });

    // updateWaypoint
    builder.addCase(updateWaypoint.pending, state => {
      state.loading = true;
      state.errors = [];
    });
    builder.addCase(updateWaypoint.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.waypoints = state.waypoints?.map(waypoint => (waypoint.id === payload.waypoints[0].id ? payload.waypoints[0] : waypoint));
    });
    builder.addCase(updateWaypoint.rejected, (state, { payload }) => {
      state.loading = false;
      if (payload) {
        state.errors = state.errors.concat(payload as string[]);
      }
    });

    builder.addCase(deleteWaypoint.pending, state => {
      state.loading = true;
      state.errors = [];
    });
    builder.addCase(deleteWaypoint.fulfilled, (state, { meta }) => {
      state.loading = false;
      state.waypoints = state.waypoints?.filter(waypoint => waypoint.id !== meta.arg.waypointId);
    });
    builder.addCase(deleteWaypoint.rejected, (state, { payload }) => {
      state.loading = false;
      if (payload) {
        state.errors = state.errors.concat(payload as string[]);
      }
    });

    builder.addCase(persistWaypointOrder.pending, state => {
      state.loading = true;
      state.errors = [];
    });
    builder.addCase(persistWaypointOrder.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.waypoints = payload.waypoints;
    });
    builder.addCase(persistWaypointOrder.rejected, (state, { payload }) => {
      state.loading = false;
      if (payload) {
        state.errors = state.errors.concat(payload as string[]);
      }
    });

    builder.addCase(toggleShowAllTitles.pending, state => {
      state.loading = true;
      state.errors = [];
    });
    builder.addCase(toggleShowAllTitles.fulfilled, (state, { meta }) => {
      state.loading = false;
      state.waypoints = state.waypoints?.map(waypoint => ({ ...waypoint, waypointDisplayProperty: { showTitle: meta.arg.showTitle } }));
    });
    builder.addCase(toggleShowAllTitles.rejected, (state, { payload }) => {
      state.loading = false;
      if (payload) {
        state.errors = state.errors.concat(payload as string[]);
      }
    });

    builder.addCase(deleteAllWaypoints.pending, state => {
      state.loading = true;
      state.errors = [];
    });
    builder.addCase(deleteAllWaypoints.fulfilled, state => {
      state.loading = false;
      state.waypoints = [];
      state.atMapId = undefined;
    });
    builder.addCase(deleteAllWaypoints.rejected, (state, { payload }) => {
      state.loading = false;
      if (payload) {
        state.errors = state.errors.concat(payload as string[]);
      }
    });
  }
});

export const { reorderWaypoints, toggleShowInTranslatedLang } = waypointsSlice.actions;

export const waypoints = waypointsSlice.reducer;
